import React, { useState, useEffect } from 'react';

import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid, TextField, Avatar, Chip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import './Locations.scss';

const AssignUsersModal = ({ open, onClose, location, users, fetchAssignedUsers, onSave }) => {
  const { t } = useTranslation();

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [initialUsers, setInitialUsers] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location && open) {
      const fetchUsers = async () => {
        try {
          setLoading(true);
          const assignedUsers = await fetchAssignedUsers(location.id);
          setSelectedUsers(assignedUsers);
          setInitialUsers(assignedUsers); // Guardar los usuarios iniciales
        } catch (error) {
          console.error("Error fetching users:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchUsers();
    }
  }, [location, open, fetchAssignedUsers]);

  const handleAddUser = (event, newValue) => {
    if (newValue && !selectedUsers.some(selectedUser => selectedUser.email === newValue.email)) {
      setSelectedUsers([...selectedUsers, newValue]);
      setAutocompleteValue(null); // Clear the selected value
      setInputValue(''); // Clear the input value
    }
  };

  const handleRemoveUser = (user) => {
    setSelectedUsers(selectedUsers.filter(selectedUser => selectedUser.email !== user.email));
  };

  const handleSave = () => {
    const usersToAdd = selectedUsers.filter(user => !initialUsers.some(initialUser => initialUser.email === user.email));
    const usersToRemove = initialUsers.filter(user => !selectedUsers.some(selectedUser => selectedUser.email === user.email));
    onSave(location.id, usersToAdd, usersToRemove);
    onClose();
  };

  const handleClose = (event, reason) => {
      onClose();
  };

  // Filter available users based on the input value
  const availableUsers = users.filter(user => !selectedUsers.some(selectedUser => selectedUser.email === user.email));
  const filteredUsers = availableUsers.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    return fullName.includes(inputValue.toLowerCase());
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle><span className="roboto-regular">{t('assign_users')}</span></DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {loading ? (
              <Box py={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : (
              selectedUsers.map(user => (
                <Chip
                  key={user.email}
                  avatar={<Avatar src={user.profilePictureUrl} alt={`${user.firstName} ${user.lastName}`} />}
                  label={`${user.firstName} ${user.lastName}`}
                  onDelete={() => handleRemoveUser(user)}
                  style={{ margin: '5px' }}
                />
              ))
            )}
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={filteredUsers}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              inputValue={inputValue} // Set the input value from state
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              value={autocompleteValue} // Set the selected value
              onChange={handleAddUser}
              renderInput={(params) => <TextField {...params} label={t('search_user')} variant="outlined" />}
              isOptionEqualToValue={(option, value) => option.email === value.email}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUsersModal;
